const VOXTRAL_TRANSCRIPTION_MODES = {
  v1: {
    label: 'T1 (fastest)',
    value: 'v1',
    model: 'voxtral'
  },
  v2: {
    label: 'T2',
    value: 'v2',
    model: 'scribe_v2'
  },
  v3: {
    label: 'T3',
    value: 'v3',
    model: 'cartesia'
  }
};

// When voxtral is disabled: scribe_v2 is primary (v1), voxtral is secondary (v2)
const VOXTRAL_DISABLED_TRANSCRIPTION_MODES = {
  v1: {
    label: 'T1 (recommended)',
    value: 'v1',
    model: 'scribe_v2'
  },
  v2: {
    label: 'T2',
    value: 'v2',
    model: 'voxtral'
  },
  v3: {
    label: 'T3',
    value: 'v3',
    model: 'cartesia'
  }
};

// When soniox is enabled: soniox is primary (v1), scribe_v2 falls back to (v2)
const SONIOX_TRANSCRIPTION_MODES = {
  v1: {
    label: 'T1 (fastest)',
    value: 'v1',
    model: 'soniox'
  },
  v2: {
    label: 'T2',
    value: 'v2',
    model: 'scribe_v2'
  },
  v3: {
    label: 'T3',
    value: 'v3',
    model: 'cartesia'
  }
};

// The real-time Soniox arm: same picker, different transport behind v1. The
// model string is what tells audio_recording_service.js to stream the
// microphone to the STT worker instead of uploading a finished clip.
// Mirrors ChatForm::SONIOX_REALTIME_ENABLED_TRANSCRIPTION_MODEL_OPTIONS.
const SONIOX_REALTIME_TRANSCRIPTION_MODES = {
  v1: {
    label: 'T1 (fastest)',
    value: 'v1',
    model: 'soniox_rt'
  },
  v2: {
    label: 'T2',
    value: 'v2',
    model: 'scribe_v2'
  },
  v3: {
    label: 'T3',
    value: 'v3',
    model: 'cartesia'
  }
};

const BETA_TRANSCRIPTION_MODES = {
  v4: {
    label: 'T4 (Scribe V2)',
    value: 'v4',
    model: 'scribe_v2'
  },
  v5: {
    label: 'T5 (gpt-4o-audio-preview)',
    value: 'v5',
    model: 'gpt-4o-audio-preview-2024-12-17'
  },
  v6: {
    label: 'T6 (Whisper Turbo)',
    value: 'v6',
    model: 'whisper-turbo'
  },
  v7: {
    label: 'T7 (Gemini Pro)',
    value: 'v7',
    model: 'gemini-pro'
  },
  v8: {
    label: 'T8 (Deepgram)',
    value: 'v8',
    model: 'deepgram'
  },
  v9: {
    label: 'T9 (Elevenlabs)',
    value: 'v9',
    model: 'scribe_v1'
  },
  v10: {
    label: 'T10 (Speechmatics)',
    value: 'v10',
    model: 'speechmatics'
  }
};

// Precedence: Soniox real-time, then Soniox async, then Voxtral. The two Soniox
// arms are variants of one PostHog flag and are never both on; the ordering
// matters only for a user who is also in the older Voxtral screen, who should
// be measured on the newer provider. Mirrors
// ChatForm.transcription_mode_options in the Rails app.
export function getTranscriptionModes(voxtralEnabled = false, betaEnabled = false, sonioxEnabled = false, sonioxRealtimeEnabled = false) {
  let baseModes;
  if (sonioxRealtimeEnabled) {
    baseModes = SONIOX_REALTIME_TRANSCRIPTION_MODES;
  } else if (sonioxEnabled) {
    baseModes = SONIOX_TRANSCRIPTION_MODES;
  } else if (voxtralEnabled) {
    baseModes = VOXTRAL_TRANSCRIPTION_MODES;
  } else {
    baseModes = VOXTRAL_DISABLED_TRANSCRIPTION_MODES;
  }
  
  if (betaEnabled) {
    return { ...baseModes, ...BETA_TRANSCRIPTION_MODES };
  }
  
  return baseModes;
}

export const TRANSCRIPTION_MODES = VOXTRAL_DISABLED_TRANSCRIPTION_MODES;

export const CLIENT_PROVIDERS = [
  { label: 'Anthropic', value: 'anthropic' },
  { label: 'OpenAI', value: 'openai' },
  { label: 'Azure', value: 'azure' },
  { label: 'Groq', value: 'groq' }
];

export const CHAT_MODELS = {
  anthropic: [
    { label: 'Claude 3 Opus', value: 'claude-3-opus-20240229' },
    { label: 'Claude 3 Sonnet', value: 'claude-3-sonnet-20240229' },
    { label: 'Claude 3.5 Sonnet', value: 'claude-3-5-sonnet-latest' },
    { label: 'Claude Haiku 4.5', value: 'claude-haiku-4-5' },
    { label: 'Claude 3.7 Sonnet', value: 'claude-3-7-sonnet-latest' },
    { label: 'Claude Sonnet 4.6', value: 'claude-sonnet-4-6' }
  ],
  openai: [
    { label: 'GPT-5.1', value: 'gpt-5.1' },
    { label: 'GPT-4', value: 'gpt-4' }
  ],
  azure: [
    { label: 'GPT-5.1', value: 'gpt-5.1' },
    { label: 'GPT-4', value: 'gpt-4' }
  ],
  groq: [
    { label: 'Llama 3.1 405B (Preview)', value: 'llama-3.1-405b-reasoning' },
    { label: 'Llama 3.1 70B (Preview)', value: 'llama-3.1-70b-versatile' },
    { label: 'Llama 3.1 8B (Preview)', value: 'llama-3.1-8b-instant' },
    { label: 'Meta Llama 3 70B', value: 'llama3-70b-8192' }
  ]
};

export const AI_MODEL_OPTIONS = [
  {
    label: 'Auto (uses the optimal model given chat & settings)',
    value: 'auto',
    helpText: null,
    clientProvider: 'openai',
    model: 'gpt-5.1'
  },
  {
    label: 'V1 (fastest)',
    value: 'v1',
    helpText: null,
    clientProvider: 'openai',
    model: 'gpt-5.1'
  },
  {
    label: 'V2 (smarter, but slower)',
    value: 'v2',
    helpText: null,
    clientProvider: 'anthropic',
    model: 'claude-sonnet-4-6'
  }
];

export const VOICE_MODEL_OPTIONS = [
  { label: 'V1 (beta model, loads faster)', value: 'v1' },
  { label: 'V2 (use for most reliable pronunciation)', value: 'v2' }
];

// All known modes for lookup (beta modes have unique keys v4-v10, so no conflict)
const ALL_VOXTRAL_DISABLED_MODES = { ...VOXTRAL_DISABLED_TRANSCRIPTION_MODES, ...BETA_TRANSCRIPTION_MODES };
const ALL_VOXTRAL_MODES = { ...ALL_VOXTRAL_DISABLED_MODES, ...VOXTRAL_TRANSCRIPTION_MODES };
const ALL_SONIOX_MODES = { ...ALL_VOXTRAL_DISABLED_MODES, ...SONIOX_TRANSCRIPTION_MODES };
const ALL_SONIOX_REALTIME_MODES = { ...ALL_VOXTRAL_DISABLED_MODES, ...SONIOX_REALTIME_TRANSCRIPTION_MODES };

// Same precedence as getTranscriptionModes.
export function determineModel(transcriptionMode, voxtralEnabled = false, sonioxEnabled = false, sonioxRealtimeEnabled = false) {
  let modes;
  let fallback;
  if (sonioxRealtimeEnabled) {
    modes = ALL_SONIOX_REALTIME_MODES;
    fallback = 'soniox_rt';
  } else if (sonioxEnabled) {
    modes = ALL_SONIOX_MODES;
    fallback = 'soniox';
  } else if (voxtralEnabled) {
    modes = ALL_VOXTRAL_MODES;
    fallback = 'voxtral';
  } else {
    modes = ALL_VOXTRAL_DISABLED_MODES;
    fallback = 'scribe_v2';
  }
  if (modes[transcriptionMode] && modes[transcriptionMode].model) {
    return modes[transcriptionMode].model;
  }
  return fallback;
}

// Takes a resolved model string, not a mode: the transport is a property of the
// model the flag chose, and the mode picker never names it.
export function isSonioxRealtime(model) {
  return model === SONIOX_REALTIME_TRANSCRIPTION_MODES.v1.model;
}

export function isDeepgram(transcriptionMode) {
  return ALL_VOXTRAL_DISABLED_MODES[transcriptionMode] && ALL_VOXTRAL_DISABLED_MODES[transcriptionMode].model === 'deepgram';
}

export function isSpeechmatics(transcriptionMode) {
  return ALL_VOXTRAL_DISABLED_MODES[transcriptionMode] && ALL_VOXTRAL_DISABLED_MODES[transcriptionMode].model === 'speechmatics';
}
